import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Input, Slider, Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import PlaceAutocomplete from '../PlaceAutocomplete/PlaceAutocomplete';
import FoodAutocomplete from '../FoodAutocomplete/FoodAutocomplete';
import { FiSearch, FiChevronDown } from 'react-icons/fi';
import { ReviewSortBy, ReviewListFilterValues, SortOrder } from '~/types/reviews';

interface ReviewListFilterProps {
  onFilterChange?: (values: ReviewListFilterValues) => void;
  onApply: (values: ReviewListFilterValues) => void;
  defaultValues?: ReviewListFilterValues;
  showPlaceAutocomplete?: boolean;
  onSortChange?: (sortBy: ReviewSortBy, sortOrder: SortOrder) => void;
}

export const ReviewListFilter: React.FC<ReviewListFilterProps> = ({
  onFilterChange,
  onApply,
  defaultValues,
  showPlaceAutocomplete = false,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const { control, watch, getValues } = useForm<ReviewListFilterValues>({
    defaultValues: {
      minRating: 0,
      maxRating: 10,
      radiusInKm: 5,
      minCost: 0,
      maxCost: 200,
      sortBy: ReviewSortBy.CREATED_AT,
      sortOrder: SortOrder.DESC,
      ...defaultValues,
    },
  });

  // Watch all form values and trigger onFilterChange
  React.useEffect(() => {
    if (onFilterChange) {
      const subscription = watch((value) => {
        onFilterChange(value as ReviewListFilterValues);
      });
      return () => subscription.unsubscribe();
    }
  }, [watch, onFilterChange]);

  const handleApply = () => {
    onApply(getValues());
  };

  const handleSortSelect = (key: string) => {
    const [sortBy, sortOrder] = key.split('-') as [ReviewSortBy, SortOrder];
    if (onSortChange) {
      onSortChange(sortBy, sortOrder);
    }
  };

  const getSortLabel = (sortBy: ReviewSortBy, sortOrder: SortOrder) => {
    switch (sortBy) {
      case ReviewSortBy.CREATED_AT:
        return sortOrder === SortOrder.DESC
          ? t('reviews.filters.sort.newest')
          : t('reviews.filters.sort.oldest');
      case ReviewSortBy.LIKE_COUNT:
        return sortOrder === SortOrder.DESC
          ? t('reviews.filters.sort.mostLiked')
          : t('reviews.filters.sort.leastLiked');
      case ReviewSortBy.COST:
        return sortOrder === SortOrder.DESC
          ? t('reviews.filters.sort.mostExpensive')
          : t('reviews.filters.sort.cheapest');
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6 pb-32">
      {/* Sort Dropdown */}
      <div>
        <label className="text-sm font-medium mb-2 block">
          {t('reviews.filters.sort.label')}
        </label>
        <Dropdown>
          <DropdownTrigger>
            <Button
              variant="flat"
              endContent={<FiChevronDown />}
              className="w-full justify-between"
            >
              {getSortLabel(
                getValues('sortBy') || ReviewSortBy.CREATED_AT,
                getValues('sortOrder') || SortOrder.DESC,
              )}
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Sort options"
            onAction={(key) => handleSortSelect(key as string)}
            className="min-w-[200px]"
          >
            <DropdownItem key={`${ReviewSortBy.CREATED_AT}-${SortOrder.DESC}`}>
              {t('reviews.filters.sort.newest')}
            </DropdownItem>
            <DropdownItem key={`${ReviewSortBy.CREATED_AT}-${SortOrder.ASC}`}>
              {t('reviews.filters.sort.oldest')}
            </DropdownItem>
            <DropdownItem key={`${ReviewSortBy.LIKE_COUNT}-${SortOrder.DESC}`}>
              {t('reviews.filters.sort.mostLiked')}
            </DropdownItem>
            <DropdownItem key={`${ReviewSortBy.LIKE_COUNT}-${SortOrder.ASC}`}>
              {t('reviews.filters.sort.leastLiked')}
            </DropdownItem>
            <DropdownItem key={`${ReviewSortBy.COST}-${SortOrder.DESC}`}>
              {t('reviews.filters.sort.mostExpensive')}
            </DropdownItem>
            <DropdownItem key={`${ReviewSortBy.COST}-${SortOrder.ASC}`}>
              {t('reviews.filters.sort.cheapest')}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>

      {/* Rating Range */}
      <div>
        <div className="flex gap-4 items-center mb-2">
          <Controller
            name="minRating"
            control={control}
            render={({ field: { value: minValue = 0, onChange: onMinChange } }) => (
              <Controller
                name="maxRating"
                control={control}
                render={({ field: { value: maxValue = 10, onChange: onMaxChange } }) => (
                  <div className="w-full">
                    <Slider
                      label={t('reviews.filters.rating')}
                      step={0.5}
                      minValue={0}
                      maxValue={10}
                      value={[minValue, maxValue] as number[]}
                      onChange={(value) => {
                        const [min, max] = value as number[];
                        onMinChange(min);
                        onMaxChange(max);
                      }}
                      className="max-w-full"
                    />
                  </div>
                )}
              />
            )}
          />
        </div>
      </div>

      {/* Cost Range */}
      <div>
        <div className="flex gap-4 items-center mb-2">
          <Controller
            name="minCost"
            control={control}
            render={({ field: { value: minValue = 0, onChange: onMinChange } }) => (
              <Controller
                name="maxCost"
                control={control}
                render={({ field: { value: maxValue = 200, onChange: onMaxChange } }) => (
                  <div className="w-full">
                    <Slider
                      label={t('reviews.filters.cost')}
                      step={1}
                      minValue={0}
                      maxValue={200}
                      value={[minValue, maxValue] as number[]}
                      onChange={(value) => {
                        const [min, max] = value as number[];
                        onMinChange(min);
                        onMaxChange(max);
                      }}
                      className="max-w-full"
                    />
                  </div>
                )}
              />
            )}
          />
        </div>
      </div>

      {/* Date Range */}
      <div className="flex gap-4">
        <Controller
          name="startDate"
          control={control}
          render={({ field }) => (
            <Input
              type="date"
              label={t('reviews.filters.startDate')}
              {...field}
              classNames={{
                base: "w-full",
              }}
            />
          )}
        />
        <Controller
          name="endDate"
          control={control}
          render={({ field }) => (
            <Input
              type="date"
              label={t('reviews.filters.endDate')}
              {...field}
              classNames={{
                base: "w-full",
              }}
            />
          )}
        />
      </div>

      <div className="flex flex-col gap-4 bg-content2 p-2 rounded-md">
        <label className="text-sm font-medium">{t('reviews.filters.location')}</label>
        {/* Place Autocomplete */}
        <div>
          <Controller
            name="placeId"
            control={control}
            render={({ field }) => (
              <PlaceAutocomplete
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        {/* Location Radius */}
        <div>
          <Controller
            name="radiusInKm"
            control={control}
            render={({ field }) => (
              <Slider
                label={t('reviews.filters.radius')}
                step={1}
                minValue={1}
                maxValue={50}
                value={field.value}
                onChange={field.onChange}
                className="max-w-full"
              />
            )}
          />
        </div>
      </div>

      {/* Food Autocomplete */}
      {showPlaceAutocomplete && (
        <div>
          <Controller
            name="foodId"
            control={control}
            render={({ field }) => (
              <FoodAutocomplete
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      )}

      {/* Apply Button */}
      <div className="absolute backdrop-blur-md z-10 bottom-0 left-0 right-0 p-8 border-t">
        <Button
          color="primary"
          className="w-full"
          startContent={<FiSearch />}
          onPress={handleApply}
        >
          {t('reviews.filters.apply')}
        </Button>
      </div>
    </div>
  );
}; 